import { useEffect, useRef, useState, useCallback } from 'react';
import { ebayCategoryPolicyReviewIdentity } from '@tcvs/shared';
import { parseCcgPolicyResponse, type CcgPolicyState } from './ccg-review-state';
import type { ApiResult } from '../auth/api-client';
/** One bounded fetch per mounted visit/context. Explicit reload is the only retry. */
export function useCcgPolicy(
  enabled: boolean,
  contextKey: string,
  load: (options?: { signal?: AbortSignal }) => Promise<ApiResult<unknown>>,
) {
  const [record, setRecord] = useState<{ scope: string; visit: number; state: CcgPolicyState }>({
    scope: '',
    visit: -1,
    state: { status: 'UNREQUESTED' },
  });
  const [revision, setRevision] = useState(0);
  const latest = useRef({ contextKey, enabled, load, revision, visit: 0 });
  if (
    latest.current.contextKey !== contextKey ||
    latest.current.enabled !== enabled ||
    latest.current.load !== load ||
    latest.current.revision !== revision
  )
    latest.current = { contextKey, enabled, load, revision, visit: latest.current.visit + 1 };
  const visit = latest.current.visit;
  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    let live = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const watchdog = setTimeout(() => {
      if (!live || latest.current.visit !== visit) return;
      controller.abort();
      setRecord({ scope: contextKey, visit, state: { status: 'UNAVAILABLE' } });
    }, 15_000);
    setRecord({ scope: contextKey, visit, state: { status: 'LOADING' } });
    void Promise.resolve()
      .then(() => {
        if (!live || controller.signal.aborted) throw new Error('Policy visit ended.');
        return load({ signal: controller.signal });
      })
      .then((result) => {
        if (!live || controller.signal.aborted || latest.current.visit !== visit) return;
        clearTimeout(watchdog);
        try {
          if (!result.ok) throw new Error('Policy unavailable');
          const v = parseCcgPolicyResponse(result.data);
          setRecord({
            scope: contextKey,
            visit,
            state: {
              status: 'AVAILABLE',
              policy: v.policy,
              reviewIdentity: ebayCategoryPolicyReviewIdentity(v.policy.binding),
              limits: v.limits,
            },
          });
          timer = setTimeout(
            () => {
              if (live) setRecord({ scope: contextKey, visit, state: { status: 'EXPIRED' } });
            },
            Math.max(1, Date.parse(v.policy.binding.expiresAt) - Date.now()),
          );
        } catch {
          setRecord({ scope: contextKey, visit, state: { status: 'UNAVAILABLE' } });
        }
      })
      .catch(() => {
        clearTimeout(watchdog);
        if (live && !controller.signal.aborted)
          setRecord({ scope: contextKey, visit, state: { status: 'UNAVAILABLE' } });
      });
    return () => {
      live = false;
      clearTimeout(watchdog);
      controller.abort();
      if (timer) clearTimeout(timer);
    };
  }, [enabled, contextKey, revision, visit, load]);
  const reload = useCallback(() => setRevision((v) => v + 1), []);
  return {
    state: !enabled
      ? { status: 'UNREQUESTED' as const }
      : record.scope === contextKey && record.visit === visit
        ? record.state
        : { status: 'LOADING' as const },
    reload,
  };
}
